import React, { useState, useCallback } from 'react';
import cx from 'classnames';

import useThemeBlock from '@/hooks/useThemeBlock';
import { Section } from '@/helpers/Section';
import { ButtonSecondary } from '@/components/atoms/ButtonSecondary/render';
import { Image } from '@/components/molecules/Image/render';
import { LinkProps } from '@/helpers/Link/render';
import { ListHighlightedProductions } from '@/components/renders';
import { CardProductionType } from '@/components/molecules/CardProduction/render';
import { getStyles as getSectionStyles } from '@/components/organisms/SectionOneColumn/styles.css';
import { HighProdProvider } from '@/contexts/high-prod-context';

import { getStyles } from './styles.css';
import block from './block.json';

/**
 * TYPINGS
 */
type ProductionProps = CardProductionType & {
	background?: Pick<ImageProps, 'src' | 'alt' | 'width' | 'height'> & {
		id: number;
		type: 'image' | 'video';
	};
};
type SectionHighlightedProductionsProps = SectionOneColumnProps & {
	link?: LinkProps;
	productions: Array<ProductionProps>;
};

interface ForwardedSectionHighlightedProductions
	extends React.ForwardRefExoticComponent<
			SectionHighlightedProductionsProps &
				React.RefAttributes<HTMLDivElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const SectionHighlightedProductions = React.forwardRef<
	HTMLDivElement,
	SectionHighlightedProductionsProps
>(
	(
		{
			anchor,
			className,
			title,
			link,
			productions,
			slug,
			variant: currentVariant = 'dark',
			titleTag = 'h2',
			theme = {},
			getStyles: getDfltStyles = getSectionStyles,
		},
		ref
	) => {
		const { variant, blockTheme } = useThemeBlock(
			slug,
			theme,
			currentVariant
		);

		const trimmedSlug = slug.split('/');
		const modifierSlug = trimmedSlug[trimmedSlug.length - 1];

		const [activeId, setActiveId] = useState(-1);

		const rootClass = cx(
			'supt-section supt-section--oneCol',
			`supt-section--${modifierSlug}`,
			'supt-section--colored',
			getDfltStyles(blockTheme),
			getStyles(blockTheme),
			className
		);

		const Background = useCallback(
			({ bg, productionId }) => {
				if (!bg) return null;

				if (bg.type === 'image') {
					return (
						<Image
							sizes="100vw"
							{...bg}
							layout="fill"
							className={cx(
								'supt-section__background__media',
								'supt-section__background__media--image',
								{
									'supt-section__background__media--active':
										activeId === productionId,
								}
							)}
						/>
					);
				} else if (bg.type === 'video') {
					return (
						<video
							className={cx(
								'supt-section__background__media',
								'supt-section__background__media--video',
								{
									'supt-section__background__media--active':
										activeId === productionId,
								}
							)}
							src={bg.src as string}
							autoPlay
							loop
							muted
							playsInline
						/>
					);
				}

				return null;
			},
			[activeId]
		);

		return productions?.length ? (
			<Section
				id={anchor}
				className={rootClass}
				variant={variant}
				slug={slug}
				ref={ref}
			>
				<HighProdProvider activeId={activeId} setActiveId={setActiveId}>
					<div className={cx('supt-section__background')}>
						{productions?.map(
							({ background, ...production }, index) => (
								<Background
									key={index}
									bg={background}
									productionId={production.id}
								/>
							)
						)}
					</div>
					<div className="supt-section__inner">
						{(title || link?.href) && (
							<div className="supt-section__headline">
								{title && (
									<Section.Title
										text={title}
										titleTag={titleTag}
									/>
								)}

								{link?.href ? (
									<div className="supt-section__linkWrapper">
										<ButtonSecondary {...link} />
									</div>
								) : null}
							</div>
						)}
						<div className="supt-section__content">
							<ListHighlightedProductions
								productions={productions}
							/>
						</div>
					</div>
				</HighProdProvider>
			</Section>
		) : null;
	}
) as ForwardedSectionHighlightedProductions;

SectionHighlightedProductions.slug = block.slug;
SectionHighlightedProductions.title = block.title;
